// Copyright (c) 2026 aiApas LLC. All Rights Reserved.
// GuardRok — Proprietary and Confidential. Patent Pending.
// Unauthorized use or distribution is prohibited.

import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { LangCode, LANGUAGES, TranslationKeys } from './translations'
import t from './translations'

interface LangContextValue {
  lang: LangCode
  setLang: (lang: LangCode) => void
  T: TranslationKeys
  dir: 'ltr' | 'rtl'
}

const LangContext = createContext<LangContextValue>({
  lang: 'en',
  setLang: () => {},
  T: t['en'],
  dir: 'ltr',
})

const STORAGE_KEY = 'gr_lang'

function detectBrowserLang(): LangCode {
  try {
    const nav = navigator.languages ?? [navigator.language]
    for (const raw of nav) {
      const code = raw.split('-')[0].toLowerCase() as LangCode
      if (LANGUAGES.some(l => l.code === code)) return code
    }
  } catch {}
  return 'en'
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<LangCode>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY) as LangCode | null
      if (stored && LANGUAGES.some(l => l.code === stored)) return stored
    } catch {}
    return detectBrowserLang()
  })

  const dir = LANGUAGES.find(l => l.code === lang)?.dir ?? 'ltr'

  function setLang(code: LangCode) {
    setLangState(code)
    try {
      localStorage.setItem(STORAGE_KEY, code)
    } catch {}
  }

  // Apply dir to <html> element for RTL support
  useEffect(() => {
    document.documentElement.setAttribute('dir', dir)
    document.documentElement.setAttribute('lang', lang)
  }, [lang, dir])

  return (
    <LangContext.Provider value={{ lang, setLang, T: t[lang], dir }}>
      {children}
    </LangContext.Provider>
  )
}

/** Main hook — use in any component that needs translated strings */
export function useLang() {
  return useContext(LangContext)
}
